import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetCartByGuidDTO from "../../../models/DTO/IOnGetCartByGuidDTO";
import IGetCartByGuidDTO from "../../../models/DTO/IGetCartByGuidDTO";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";
import ICart from "../../../models/ICart";
import OwTitle from "../../shared/OwTitle/OwTitle";
import OwLabel from "../../shared/OwLabel/OwLabel";
import IOrder from "../../../models/IOrder";
import AddressDisplay from "../../shared/AddressDisplay/AddressDisplay";
import { NumericFormat } from "react-number-format";
import { Link } from "react-router-dom";
import HelperService from "../../../services/HelperService";
import UserService from "../../../services/UserService";
import { showsDeliveryDate } from "../../business/BusinessSettings/DeliveryDeadlines/deliveryDateRulesUtils";
import { SocketContext } from "../../../contexts/SocketContext";
import { useCart } from "../../../contexts/CartContext";
import CartStatusEnum from "../../../models/CartStatusEnum";
import OwButton from "../../shared/OwButton/OwButton";
import "./EcommerceCheckoutComplete.scss";

// A Stripe checkout lands here as soon as Stripe confirms the payment, but the order is created by the
// Stripe webhook a moment later. Re-fetch until the order exists (or the payment turns out to have failed).
const ORDER_POLL_INTERVAL_MS = 1000;
const ORDER_POLL_MAX_ATTEMPTS = 30;
// A lookup with no reply (dropped socket, or the API answered with a Notify error) counts as a poll attempt.
const ORDER_LOOKUP_TIMEOUT_MS = 5000;
const FAILED_CART_STATUSES: string[] = [CartStatusEnum.PAYMENT_ERRORED, CartStatusEnum.FAILED];

type Props = {};

export const EcommerceCheckoutComplete: React.FC<Props> = (props) => {
    const location = useLocation();
    const params = useParams();
    const history = useNavigate();

    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);

    const hidePricing = UserService.hidePricing(supplierModel, userModel, vendorModel);

    const socket = useContext(SocketContext)!;
    const { refreshInactiveCarts } = useCart();

    const [cartGuid, setCartGuid] = useState<string>();
    const [cart, setCart] = useState<ICart>();
    const [order, setOrder] = useState<IOrder>();

    const [waitingForOrder, setWaitingForOrder] = useState(true);

    const pollAttemptsRef = useRef(0);
    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const lookupTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    useEffect(() => {
        const search = new URLSearchParams(location.search);

        const _cartGuid = search.get("g");

        setCartGuid(_cartGuid as string);

        // No cart to look up: fall straight through to the not-waiting state instead of spinning forever.
        if (!_cartGuid) {
            setWaitingForOrder(false);
        }

        socket.on(EndpointsEnum.CartOnGetCartByGuid, onGetCartByGuid);

        document.title = "Orderwerks >> Checkout Complete";

        return () => {
            socket.removeListener(EndpointsEnum.CartOnGetCartByGuid, onGetCartByGuid);
            clearTimeout(pollTimerRef.current);
            clearTimeout(lookupTimerRef.current);
        };
    }, []);

    useEffect(() => {
        if (cartGuid?.length) {
            getCartByGuid(cartGuid);
        }
    }, [cartGuid]);

    useEffect(() => {
        if (order) {
            document.title = `Orderwerks >> Checkout Complete >> ${order.name}`;
        }
    }, [order]);

    // Takes the guid rather than reading state: the socket listener registered on mount re-calls this.
    const getCartByGuid = (guid: string) => {
        const payload: IGetCartByGuidDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            cartGuid: guid,
        };

        socket.emit(EndpointsEnum.CartGetCartByGuid, payload);

        clearTimeout(lookupTimerRef.current);
        lookupTimerRef.current = setTimeout(() => scheduleNextLookup(guid), ORDER_LOOKUP_TIMEOUT_MS);
    };

    const scheduleNextLookup = (guid: string) => {
        if (pollAttemptsRef.current < ORDER_POLL_MAX_ATTEMPTS) {
            pollAttemptsRef.current++;
            pollTimerRef.current = setTimeout(() => getCartByGuid(guid), ORDER_POLL_INTERVAL_MS);
        } else {
            setWaitingForOrder(false);
        }
    };

    const onGetCartByGuid = (data: IOnGetCartByGuidDTO) => {
        // Any reply (including a late one after a timeout) cancels the pending step, so only one poll chain runs.
        clearTimeout(lookupTimerRef.current);
        clearTimeout(pollTimerRef.current);

        setCart(data.cart);
        setOrder(data.order);

        if (data.order || !data.cart) {
            return;
        }

        // The payment failed after checkout handed off to this page: send the buyer to the recovery page.
        if (FAILED_CART_STATUSES.includes(data.cart.status as string)) {
            refreshInactiveCarts();
            history(`/ecomm/inactive-cart/${data.cart.guid}`);
            return;
        }

        scheduleNextLookup(data.cart.guid);
    };

    const viewOrder = () => {
        if (order) {
            history(`/ecomm/orders/details/${order.guid}`);
        }
    };

    const continueShopping = () => {
        history("/ecomm");
    };

    const pendingIconClass = waitingForOrder ? "fas fa-circle-notch fa-spin" : "far fa-clock";

    return (
        <div className={"EcommerceCheckoutComplete"}>
            <div className={"checkout-complete-card"}>
                <div className={"checkout-complete-hero"}>
                    <div className={`checkout-complete-icon ${order ? "" : "is-pending"}`}>
                        <i className={order ? "fas fa-check" : pendingIconClass} />
                    </div>
                    <OwTitle
                        title={order ? "checkout_successful" : "pending_cart_processing"}
                        tag={"h3"}
                        className={"checkout-complete-title"}
                    />
                    {order ? (
                        <OwLabel title={"checkout_complete_message"} className={"checkout-complete-subtitle"} noBottomMargin={true} />
                    ) : null}
                </div>

                {order ? (
                    <dl className={"checkout-complete-details"}>
                        <div className={"checkout-complete-row"}>
                            <dt>
                                <OwLabel title={"order_no"} noBottomMargin={true} />
                            </dt>
                            <dd>
                                <Link className={"checkout-complete-order-link"} to={`/ecomm/orders/details/${order.guid}`}>
                                    {order.name}
                                </Link>
                            </dd>
                        </div>
                        <div className={"checkout-complete-row"}>
                            <dt>
                                <OwLabel title={"ship_to"} noBottomMargin={true} />
                            </dt>
                            <dd>
                                <AddressDisplay address={order.Ship_To} showName={true} showCompanyName={true} />
                            </dd>
                        </div>
                        {showsDeliveryDate(order, supplierModel) && (
                            <div className={"checkout-complete-row"}>
                                <dt>
                                    <OwLabel title={"delivery_date"} noBottomMargin={true} />
                                </dt>
                                <dd>{UserService.parseUTCDate(order.delivery_date)}</dd>
                            </div>
                        )}
                        {!hidePricing ? (
                            <div className={"checkout-complete-row is-total"}>
                                <dt>
                                    <OwLabel title={"total"} noBottomMargin={true} />
                                </dt>
                                <dd>
                                    {HelperService.formatCurrency({
                                        value: order.grand_total,
                                        currency: "USD",
                                        precision: supplierModel.pricing_precision || 2,
                                    })}
                                </dd>
                            </div>
                        ) : null}
                    </dl>
                ) : null}

                {/* Secondary action first: it sits left of the primary on wide screens and below it on phones. */}
                <div className={"checkout-complete-actions"}>
                    <OwButton
                        title={"continue_shopping"}
                        className={order ? "checkout-complete-btn-secondary" : "checkout-complete-btn-primary"}
                        btnSizeClass={"btn-lg"}
                        fn={continueShopping}
                    />
                    {order ? (
                        <OwButton
                            title={"order_details"}
                            className={"checkout-complete-btn-primary"}
                            btnSizeClass={"btn-lg"}
                            leftIconClass={"fas fa-receipt"}
                            fn={viewOrder}
                        />
                    ) : null}
                </div>
            </div>
        </div>
    );
};
